import { useCallback } from 'react';
import { useAuthStore } from '../../contexts/auth/useAuthStore';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useUser } from '../useUser';
import { Flow } from '../useFlowsData';

const WEBHOOK_URL = 'https://hooks.zapier.com/hooks/catch/12395623/u05u7x8/';

interface HubspotData {
  contact_url?: string;
}

export const useReviveEngageWebhook = () => {
  const { user: authUser } = useAuthStore();
  const { user: firestoreUser } = useUser();
  const { organization } = useOrganizationStore();

  const sendWebhook = useCallback(
    async (flow: Flow, action: 'paused' | 'scheduled') => {
      try {
        const hubspotData = (organization as unknown as { hubspot?: HubspotData })?.hubspot;
        const userEmail = firestoreUser?.email || authUser?.email || '';
        const userFullName =
          firestoreUser?.displayName ||
          (firestoreUser?.firstName && firestoreUser?.lastName
            ? `${firestoreUser.firstName} ${firestoreUser.lastName}`
            : firestoreUser?.firstName) ||
          authUser?.displayName ||
          '';
        const userPhone = firestoreUser?.phoneNumber || '';

        const webhookPayload = {
          // Action taken
          action,

          // General org info
          organizationName: organization?.org_name || '',
          organizationId: organization?.id || '',
          hubspotContactUrl: hubspotData?.contact_url || '',
          dateTime: new Date().toISOString(),

          // User info
          userEmail,
          userFullName,
          userPhone,

          // Flow info
          flowId: flow.id,
          flowName: flow.name,
          flowType: flow.type,
          flowSubType: flow.sub_type,
          flowStatus: flow.status,
          flowDescription: flow.description || '',
          flowTimezone: flow.timezone || '',
          flowCountryCode: flow.country_code || '',
          scheduledFor: flow.scheduled_for || '',
          schedulingWindow: flow.scheduling_window ?? null,
          scheduleOverUnit: flow.schedule_over_unit || '',
          allowedDays: flow.allowed_days || [],
          allowedHours: flow.allowed_hours || null,
          excludeHolidays: flow.exclude_holidays ?? false,
          useRoundRobin: flow.useRoundRobin ?? false,
          transferNumbers: flow.transfer_numbers || {},
          createdAt: flow.created_at || '',
        };

        await fetch(WEBHOOK_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(webhookPayload),
        });

        console.log('Revive/Engage webhook sent:', action, webhookPayload);
      } catch (error) {
        console.error('Revive/Engage webhook error:', error);
        // Don't block the main operation on webhook failure
      }
    },
    [authUser, firestoreUser, organization]
  );

  return { sendWebhook };
};
